import { Container, Grid, Paper, Typography, Card, CardContent, Box } from "@mui/material";
import { IPDCWelcomeMessage } from "../../../../components";
import "./DACDashboard.css";
import { BPRequestVsResponseCard, OPRequestVsResponseCard, BPRequestResponseParkCard } from "../Head/DashboardDetail";
import ViewMessageList from "../../Shared/Message/ViewMessageList";
import { useEffect, useState } from "react";
import { GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests";
import {
  BarChart as BarChartIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationOnIcon
} from "@mui/icons-material";

// Format number with commas
const formatNumber = (number) => {
  if (!number && number !== 0) return "0";
  return new Intl.NumberFormat('en-US').format(number);
};

export default function DaMDashboard() {
  const [buildingStats, setBuildingStats] = useState({
    totalRequests: 0,
    totalIssued: 0,
    totalPending: 0,
    totalOccupancyRequests: 0,
    totalResidentialBuildings: 0,
    totalCommercialBuildings: 0
  });
  const [occupancyStats, setOccupancyStats] = useState({
    totalRequests: 0,
    totalApproved: 0,
    totalPending: 0,
    approvalRate: 0,
    averageCompletionRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [buildingPermits, setBuildingPermits] = useState([]);
  const [occupancyPermits, setOccupancyPermits] = useState([]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [buildingRes, occupancyRes] = await Promise.all([
          GetDataFromApiWithParams(`design-management/building-permits1`),
          GetDataFromApiWithParams(`design-management/occupancy-permits`)
        ]);

        let buildingData = [];
        let occupancyData = [];

        if (buildingRes && Array.isArray(buildingRes)) buildingData = buildingRes;
        else if (buildingRes?.data && Array.isArray(buildingRes.data)) buildingData = buildingRes.data;

        if (occupancyRes && Array.isArray(occupancyRes)) occupancyData = occupancyRes;
        else if (occupancyRes?.data && Array.isArray(occupancyRes.data)) occupancyData = occupancyRes.data;

        const formattedBuildingPermits = buildingData.map(permit => ({
          id: permit.id,
          permit_no: permit.permit_no || `BP-${permit.id}`,
          status: permit.status || "Pending",
          no_of_residence_bldgs: permit.no_of_residence_bldgs || 0,
          no_of_commercial_bldgs: permit.no_of_commercial_bldgs || 0,
          requested_on: permit.requested_on,
          park_name: permit.park_name || "N/A"
        }));

        const formattedOccupancyPermits = occupancyData.map(occ => ({
          id: occ.id,
          building_permit_id: occ.building_permit_id,
          status: occ.status || "Pending",
          requested_on: occ.requested_on
        }));

        setBuildingPermits(formattedBuildingPermits);
        setOccupancyPermits(formattedOccupancyPermits);

        // Calculate Building Permit Statistics
        const totalBuildingRequests = formattedBuildingPermits.length;
        const totalIssued = formattedBuildingPermits.filter(p => p.status === "Finished" || p.status === "Approved" || p.status === "Issued").length;
        const totalBuildingPending = formattedBuildingPermits.filter(p => p.status === "REQUESTED" || p.status === "Pending" || p.status === "Under Review").length;
        const totalResidential = formattedBuildingPermits.reduce((sum, p) => sum + (p.no_of_residence_bldgs || 0), 0);
        const totalCommercial = formattedBuildingPermits.reduce((sum, p) => sum + (p.no_of_commercial_bldgs || 0), 0);
        const totalOccupancy = formattedOccupancyPermits.length;

        setBuildingStats({
          totalRequests: totalBuildingRequests,
          totalIssued: totalIssued,
          totalPending: totalBuildingPending,
          totalOccupancyRequests: totalOccupancy,
          totalResidentialBuildings: totalResidential,
          totalCommercialBuildings: totalCommercial
        });

        // Calculate Occupancy Permit Statistics
        const totalOccupancyRequests = formattedOccupancyPermits.length;
        const totalApproved = formattedOccupancyPermits.filter(p => p.status === "Finished" || p.status === "Approved" || p.status === "approved").length;
        const totalOccupancyPending = formattedOccupancyPermits.filter(p => p.status === "REQUESTED" || p.status === "Pending" || p.status === "UNDER_REVIEW").length;
        const approvalRate = totalOccupancyRequests > 0 ? (totalApproved / totalOccupancyRequests) * 100 : 0;

        setOccupancyStats({
          totalRequests: totalOccupancyRequests,
          totalApproved: totalApproved,
          totalPending: totalOccupancyPending,
          approvalRate: approvalRate,
          averageCompletionRate: 0
        });

      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate yearly trends
  const getYearlyTrends = () => {
    const yearlyData = {};
    
    buildingPermits.forEach(permit => {
      if (permit.requested_on) {
        const year = new Date(permit.requested_on).getFullYear().toString();
        if (!yearlyData[year]) {
          yearlyData[year] = { totalRequests: 0, totalIssued: 0, totalOccupancy: 0 };
        }
        yearlyData[year].totalRequests++;
        if (permit.status === "Finished" || permit.status === "Approved" || permit.status === "Issued") {
          yearlyData[year].totalIssued++;
        }
      }
    });

    occupancyPermits.forEach(occ => {
      if (occ.requested_on) {
        const year = new Date(occ.requested_on).getFullYear().toString();
        if (yearlyData[year]) {
          yearlyData[year].totalOccupancy = (yearlyData[year].totalOccupancy || 0) + 1;
        } else if (occ.requested_on) {
          yearlyData[year] = { totalRequests: 0, totalIssued: 0, totalOccupancy: 1 };
        }
      }
    });

    return yearlyData;
  };

  // Calculate requests by park
  const getParkWiseStats = () => {
    const parkData = {};
    
    buildingPermits.forEach(permit => {
      const park = permit.park_name || "Unassigned";
      if (!parkData[park]) {
        parkData[park] = { totalRequests: 0, totalIssued: 0 };
      }
      parkData[park].totalRequests++;
      if (permit.status === "Finished" || permit.status === "Approved" || permit.status === "Issued") {
        parkData[park].totalIssued++;
      }
    });
    
    return parkData;
  };

  const yearlyTrends = getYearlyTrends();
  const parkWiseStats = getParkWiseStats();

  return (
    <Box sx={{ bgcolor: '#f4f9ff', width: '98%', mx: '1%', py: 1 }}>
      {/* Header */}
      <Box sx={{ color: 'white', py: 2, px: 3 }}>
        <Container maxWidth="xl">
          <Box>
            <Grid item xs={12} md={12}>
              <IPDCWelcomeMessage messageTitle="Design Management" />
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Building and Occupancy Permit Overview Boxes */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Building Permit Overview Box */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
                Building Permit Overview
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <AssignmentIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(buildingStats.totalRequests)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Total Building Permits</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(buildingStats.totalIssued)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Permits Issued</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <PendingIcon sx={{ color: '#ed6c02', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(buildingStats.totalPending)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Pending Requests</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Occupancy Permit Overview Box */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 2 }}>
                Occupancy Permit Overview
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <AssignmentIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(occupancyStats.totalRequests)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Total Occupancy Requests</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(occupancyStats.totalApproved)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Approved</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <PendingIcon sx={{ color: '#ed6c02', fontSize: 24 }} />
                      <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                        {formatNumber(occupancyStats.totalPending)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Pending</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Existing Cards */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <BPRequestVsResponseCard />
          </Grid>
          <Grid item xs={12} md={6}>
            <OPRequestVsResponseCard />
          </Grid>
        </Grid>
      </Container>

      {/* Park-wise Building Permit Requests Chart */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOnIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  Building Permits by Park
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {Object.keys(parkWiseStats).length > 0 ? (
                  Object.entries(parkWiseStats).map(([park, stats]) => {
                    const percentage = buildingStats.totalRequests > 0 
                      ? ((stats.totalRequests / buildingStats.totalRequests) * 100).toFixed(1) 
                      : 0;
                    return (
                      <Box key={park} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{park}</Typography>
                          <Typography variant="body2" color="textSecondary">{stats.totalRequests} requests ({percentage}%)</Typography>
                        </Box>
                        <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 8, overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              width: `${percentage}%`, 
                              bgcolor: '#1976d2', 
                              height: '100%',
                              borderRadius: 1
                            }} 
                          />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                    No park data available
                  </Typography>
                )}
              </Box>
              <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="textSecondary">
                  Total Parks with requests: {Object.keys(parkWiseStats).length}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Yearly Trend Summary */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  Yearly Trend Summary
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {Object.keys(yearlyTrends).length > 0 ? (
                  Object.entries(yearlyTrends)
                    .sort((a, b) => a[0] - b[0])
                    .map(([year, data]) => (
                      <Box key={year} sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a237e' }}>{year}</Typography>
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary">Total Requests</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                              {formatNumber(data.totalRequests)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="textSecondary">Permits Issued</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                              {formatNumber(data.totalIssued || 0)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                    No yearly data available
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Additional Components */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BPRequestResponseParkCard />
          </Grid>
        </Grid>
      </Container>


     
    </Box>
  );
}